/**
 * Smart-guide alignment snapping between sketch objects (FREE placements).
 *
 * While placing or dragging a free object, its world-axis bounding-box
 * features (left/center/right and bottom/center/top) snap to the matching
 * features of every other visible sketch object when they come within a few
 * pixels — Figma/PowerPoint-style. X and Y snap independently; each snapped
 * axis returns an {@link AlignGuide} line for the tool to render, spanning
 * both objects so the user sees what aligned with what.
 */

import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import type { Viewer } from '../../viewer/viewer';
import type { PixelHitTester } from '../../personas/tools/modules/selection/PixelHitTester';
import type { SketchObjectFrame } from '../types';
import { isAnnotationPickBlocked } from '../../viewer/AnnotationBlacklistStack';

/** One rendered alignment guide line (world XY, on the floor plane). */
export interface AlignGuide {
  a: Pt2;
  b: Pt2;
}

/** Result of {@link snapToPeerObjects}: the (possibly adjusted) center. */
export interface AlignSnapResult {
  x: number;
  y: number;
  guides: AlignGuide[];
}

/** Screen-space capture distance of an alignment feature. */
const SNAP_THRESHOLD_PX = 8;

/** The best feature match found on one world axis. */
interface AxisMatch {
  /** Shift to apply to the moving center so the features coincide. */
  delta: number;
  /** The matched feature coordinate (where the guide line is drawn). */
  line: number;
  /** Peer's center + half extent on the OTHER axis (guide line span). */
  peerCenter: number;
  peerHalf: number;
}

/** World-axis half extents of a (rotated) frame footprint. */
function frameAxisExtents(frame: SketchObjectFrame): { hx: number; hy: number } {
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  return {
    hx: Math.abs(frame.dir.x) * hw + Math.abs(frame.norm.x) * hd,
    hy: Math.abs(frame.dir.y) * hw + Math.abs(frame.norm.y) * hd,
  };
}

/** lo / center / hi feature coordinates of a span on one axis. */
function features(center: number, half: number): [number, number, number] {
  return [center - half, center, center + half];
}

/**
 * Snap a free object's candidate center to nearby peers' alignment features.
 *
 * @param selfId  identifier of the object being dragged (excluded from the
 *                peer scan), or null while placing a new object
 * @param center  candidate world center (cursor-resolved, pre-snap)
 * @param rotation/width/depth  the moving object's footprint
 */
export function snapToPeerObjects(
  viewer: Viewer,
  hitTester: PixelHitTester,
  selfId: string | null,
  center: Pt2,
  rotation: number,
  width: number,
  depth: number,
): AlignSnapResult {
  const unsnapped: AlignSnapResult = { x: center.x, y: center.y, guides: [] };

  // Pixel scale at the candidate point — the threshold is a SCREEN distance.
  const p0 = hitTester.projectWorldToPixel({ x: center.x, y: center.y, z: 0 });
  const p1 = hitTester.projectWorldToPixel({ x: center.x + 1, y: center.y, z: 0 });
  if (!p0 || !p1) return unsnapped;
  const pxPerMeter = p0.distanceTo(p1);
  if (pxPerMeter < 1e-6) return unsnapped;
  const threshold = SNAP_THRESHOLD_PX / pxPerMeter;

  const dir: Pt2 = { x: Math.cos(rotation), y: Math.sin(rotation) };
  const self = frameAxisExtents({ center, dir, norm: { x: -dir.y, y: dir.x }, width, depth, z: 0 });
  const selfXs = features(center.x, self.hx);
  const selfYs = features(center.y, self.hy);

  let bestX: AxisMatch | null = null;
  let bestY: AxisMatch | null = null;
  for (const obj of viewer.scene.sketchObjects) {
    if (!obj.visible || obj.identifier === selfId) continue;
    // Per-floor hiding only flips `.visible` DURING a frame (render blacklist),
    // so between frames other floors' objects still read visible — without
    // this check the ghost would align against invisible peers on other floors.
    if (isAnnotationPickBlocked(viewer, obj, 'SKETCH_OBJECT')) continue;
    const frame = obj.resolveFrame();
    if (!frame) continue;
    const ext = frameAxisExtents(frame);
    for (const peerX of features(frame.center.x, ext.hx)) {
      for (const selfX of selfXs) {
        const delta = peerX - selfX;
        if (Math.abs(delta) <= threshold && (!bestX || Math.abs(delta) < Math.abs(bestX.delta))) {
          bestX = { delta, line: peerX, peerCenter: frame.center.y, peerHalf: ext.hy };
        }
      }
    }
    for (const peerY of features(frame.center.y, ext.hy)) {
      for (const selfY of selfYs) {
        const delta = peerY - selfY;
        if (Math.abs(delta) <= threshold && (!bestY || Math.abs(delta) < Math.abs(bestY.delta))) {
          bestY = { delta, line: peerY, peerCenter: frame.center.x, peerHalf: ext.hx };
        }
      }
    }
  }
  if (!bestX && !bestY) return unsnapped;

  const x = center.x + (bestX?.delta ?? 0);
  const y = center.y + (bestY?.delta ?? 0);
  const guides: AlignGuide[] = [];
  if (bestX) {
    guides.push({
      a: { x: bestX.line, y: Math.min(y - self.hy, bestX.peerCenter - bestX.peerHalf) },
      b: { x: bestX.line, y: Math.max(y + self.hy, bestX.peerCenter + bestX.peerHalf) },
    });
  }
  if (bestY) {
    guides.push({
      a: { x: Math.min(x - self.hx, bestY.peerCenter - bestY.peerHalf), y: bestY.line },
      b: { x: Math.max(x + self.hx, bestY.peerCenter + bestY.peerHalf), y: bestY.line },
    });
  }
  return { x, y, guides };
}
